import type { ReactNode } from "react";
import type { CSSProperties } from "react";
import type { LucideIcon } from "lucide-react";

/**
 * Home-screen overview card (spec §2.1): white 22px-radius card with an icon
 * chip, an optional top-right accessory, then title / big value / status line.
 */
export function Card({
  icon: Icon,
  tint,
  chipOpacity = 0.12,
  topRight,
  title,
  value,
  valueSuffix,
  dotColor,
  statusText,
  activeBg,
  activeBorder,
  disabled = false,
  dimmed = false,
  onPress,
  ariaLabel
}: {
  icon: LucideIcon;
  /** CSS color for the icon + chip (e.g. "var(--cool)"). */
  tint: string;
  chipOpacity?: number;
  topRight?: ReactNode;
  title: string;
  value: string;
  valueSuffix?: string;
  dotColor: string;
  statusText: string;
  /** Card background when the card is in its "active" state. */
  activeBg?: string;
  /** Border color when active (tint at 34%). */
  activeBorder?: string;
  /** 0.58 opacity and unclickable. */
  disabled?: boolean;
  /** 0.58 opacity but still tappable (e.g. music unavailable). */
  dimmed?: boolean;
  onPress?: () => void;
  ariaLabel?: string;
}) {
  const style: CSSProperties = {};
  if (activeBg) style.background = activeBg;
  if (activeBorder) style.borderColor = activeBorder;
  if (dimmed) style.opacity = "var(--disabled-opacity)";

  return (
    <button
      type="button"
      className="ov-card"
      style={style}
      disabled={disabled}
      onClick={onPress}
      aria-label={ariaLabel ?? title}
    >
      <span className="ov-top">
        <span
          className="ov-chip"
          style={{ color: tint, background: `color-mix(in srgb, ${tint} ${chipOpacity * 100}%, transparent)` }}
        >
          <Icon size={22} strokeWidth={2.1} />
        </span>
        {topRight}
      </span>
      <span className="ov-text">
        <span className="ov-title">{title}</span>
        <span className="ov-value">
          {value}
          {valueSuffix ? <span className="ov-suffix">{valueSuffix}</span> : null}
        </span>
        <span className="ov-status">
          <span className="ov-dot" style={{ background: dotColor }} />
          <span className="ov-status-text">{statusText}</span>
        </span>
      </span>
    </button>
  );
}

/** Capsule badge used on the Tesla and Pool cards (spec §2.3/§2.4). */
export function Badge({ text, bg }: { text: string; bg: string }) {
  return (
    <span className="badge" style={{ background: bg }}>
      {text}
    </span>
  );
}
